import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AVMark } from "../components/Logo";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const [pin, setPin] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!pin) return setError("Please enter your code");
    setLoading(true);
    setError("");
    try {
      await login(pin);
      navigate("/");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Invalid code");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden px-4">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(hsl(211 100% 50%) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <form onSubmit={submit} className="relative w-full max-w-sm fade-up">
        <div className="flex flex-col items-center mb-10">
          <AVMark size={68} />
          <h1
            className="text-3xl font-display font-extrabold tracking-tight mt-6"
            style={{
              background: "linear-gradient(95deg, #FFFFFF 0%, #BFE2FF 55%, #19E3FF 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
            }}
          >
            SignHub
          </h1>
          <p className="text-[11px] uppercase tracking-[0.32em] text-muted-foreground mt-2">Powered by AV Tech</p>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground/70 mt-3">TV Control Center</p>
        </div>

        <div className="bg-card border border-border rounded-md p-6">
          <Label htmlFor="pin" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Access Code</Label>
          <div className="relative mt-2">
            <Input
              id="pin"
              data-testid="pin-input"
              type={show ? "text" : "password"}
              value={pin}
              onChange={(e) => { setPin(e.target.value); setError(""); }}
              placeholder="Enter your code"
              autoFocus
              className="h-12 pr-11 text-lg font-mono tracking-[0.2em]"
            />
            <button
              type="button"
              data-testid="toggle-visibility"
              onClick={() => setShow((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
            >
              {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {error && (
            <p data-testid="login-error" className="text-destructive text-sm mt-3">{error}</p>
          )}

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="w-full h-12 mt-5 text-base font-medium"
          >
            {loading ? "Verifying…" : "Unlock Dashboard"}
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Setting up a TV? Open <span className="text-primary font-mono">/player</span> on the screen.
        </p>
      </form>
    </div>
  );
}
